import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import ClockIcon from '@atlaskit/icon/core/clock';
import type {
	ObjectListFlyoutAction,
	ObjectListFlyoutItemConfig,
} from '@atlassian/navigation-common/types';

import type { ForYouConfig } from '../../../common/forYouTypes';
import type { NavItemConfig } from '../../../common/types';
import { omitSafely } from '../../../common/utils/omit-safely';

import { RECENT_TEST_ID } from './constants';
import { messages } from './messages';

const elemBefore = <ClockIcon label="" />;

export function useRecentNavItem(forYou: ForYouConfig): [string, NavItemConfig][] {
	const { formatMessage } = useIntl();
	const recentConfig = forYou?.recent;

	return useMemo(() => {
		if (!recentConfig) {
			return [];
		} else {
			// This is where we map the forYou config to the ObjectListFlyout config.
			const actions: ObjectListFlyoutAction[] | undefined = recentConfig.viewAllUrl
				? [
						{
							elemBefore: <AlignTextLeftIcon label={''} />,
							label: formatMessage(messages.viewAllRecentItemsLabel),
							href: recentConfig.viewAllUrl,
							onClick: recentConfig.viewAllOnClick,
						},
					]
				: recentConfig.viewAllOnClick && [
						{
							elemBefore: <AlignTextLeftIcon label={''} />,
							label: formatMessage(messages.viewAllRecentItemsLabel),
							onClick: recentConfig.viewAllOnClick,
						},
					];

			// Omit the type-props from FlyoutProps that are not used by ObjectListFlyout
			const safeConfig = omitSafely(recentConfig, 'menuItemId', 'viewAllOnClick', 'viewAllUrl');

			const value: ObjectListFlyoutItemConfig = {
				...safeConfig,
				actions,
				elemBefore,
				label: formatMessage(messages.label),
				searchPlaceholder: formatMessage(messages.searchPlaceholder),
				testId: RECENT_TEST_ID,
				type: 'object-list-flyout',
			};

			return [[recentConfig.menuItemId ?? 'global.sidebar.recent', value]];
		}
	}, [formatMessage, recentConfig]);
}
